import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { createContext, memo, ReactNode, useContext, useState } from "react";
import { PackingSlipBinModal } from "./packing-slip-bin-modal";

export type OpenPackingSlipBinModalFn = (
  productsWithoutBins: FulfillmentOrderLineItem[],
  onProceedWithoutBin: () => void,
  onSaveAndContinue: () => void,
  onExit: () => void,
) => void;

export const PackingSlipBinModalContext = createContext<
  OpenPackingSlipBinModalFn | undefined
>(undefined);

export const usePackingSlipBinModal = (): OpenPackingSlipBinModalFn => {
  const context = useContext(PackingSlipBinModalContext);
  if (!context) {
    throw new Error(
      "usePackingSlipBinModal must be used within PackingSlipBinModalProvider",
    );
  }
  return context;
};

export const PackingSlipBinModalProvider = memo(
  function PackingSlipBinModalProvider({ children }: { children: ReactNode }) {
    const [modalState, setModalState] = useState<{
      open: boolean;
      productsWithoutBins: FulfillmentOrderLineItem[];
      onProceedWithoutBin: () => void;
      onSaveAndContinue: () => void;
      onExit: () => void;
    }>({
      open: false,
      productsWithoutBins: [],
      onProceedWithoutBin: () => {},
      onSaveAndContinue: () => {},
      onExit: () => {},
    });

    const openModal: OpenPackingSlipBinModalFn = (
      productsWithoutBins,
      onProceedWithoutBin,
      onSaveAndContinue,
      onExit,
    ) => {
      setModalState({
        open: true,
        productsWithoutBins,
        onProceedWithoutBin,
        onSaveAndContinue,
        onExit,
      });
    };

    const closeModal = () => {
      setModalState((prev) => ({ ...prev, open: false }));
    };

    const handleProceedWithoutBin = () => {
      closeModal();
      modalState.onProceedWithoutBin();
    };

    const handleSaveAndContinue = () => {
      closeModal();
      modalState.onSaveAndContinue();
    };

    const handleExit = () => {
      closeModal();
      modalState.onExit();
    };

    return (
      <PackingSlipBinModalContext.Provider value={openModal}>
        {children}
        <PackingSlipBinModal
          onExit={handleExit}
          onProceedWithoutBin={handleProceedWithoutBin}
          onSaveAndContinue={handleSaveAndContinue}
          open={modalState.open}
          productsWithoutBins={modalState.productsWithoutBins}
          setOpen={closeModal}
        />
      </PackingSlipBinModalContext.Provider>
    );
  },
);
